import { useState, useEffect, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useOrganizationInvites } from "@/hooks/useOrganizationInvites";
import { InviteUserDialog } from "@/components/admin/InviteUserDialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SettingsSectionLabel } from "@/components/settings/SettingsSectionLabel";
import { partitionInvites } from "@/components/settings/inviteGroups";
import {
  ExpiredInviteGroup,
  InvitationsEmptyState,
  PendingInviteGroup,
  type InviteActionHandlers,
  type OrgInvite,
} from "@/components/settings/InvitationRows";
import { Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useResendInvite } from "@/hooks/useResendInvite";
import { useCancelInvite } from "@/hooks/useCancelInvite";
import { useClearExpiredInvites } from "@/hooks/useClearExpiredInvites";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { usePermissions } from "@/hooks/usePermissions";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

export function InvitationsSection() {
  // Inviting is an org-scoped action for owners/admins — NOT platform admin.
  const { canManageMembers } = usePermissions();
  const { t } = useLanguage();
  const { data: invites, refetch: refetchInvites, isLoading, error } = useOrganizationInvites();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [inviteToDelete, setInviteToDelete] = useState<OrgInvite | null>(null);
  const [inviteToRevoke, setInviteToRevoke] = useState<OrgInvite | null>(null);
  const [confirmClearExpired, setConfirmClearExpired] = useState(false);
  const { currentOrganization } = useOrganizationContext();
  const resendMutation = useResendInvite();
  const cancelMutation = useCancelInvite();
  const clearExpiredMutation = useClearExpiredInvites();

  // Outstanding invites split by whether their link still works: pending ones are
  // actionable, expired ones can only be resent or deleted. Deliberately not
  // memoized on `invites` — the split also depends on the wall clock, so an invite
  // that crosses its expiry has to fall into the Expired group on the next render.
  const { pending, expired } = partitionInvites((invites ?? []) as OrgInvite[]);

  // Keep the list live while the org's invites change under us.
  useEffect(() => {
    if (!currentOrganization?.id) return;

    const channel = supabase
      .channel("organization_invites_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "organization_invites",
          filter: `org_id=eq.${currentOrganization.id}`,
        },
        () => {
          refetchInvites();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentOrganization?.id, refetchInvites]);

  const handleCopyLink = async (invite: OrgInvite) => {
    const url = `${window.location.origin}/invite/accept?token=${invite.invite_token}`;
    await navigator.clipboard.writeText(url);
    toast.success(t.settings.invitations.linkCopied);
    setCopiedId(invite.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Revoke (pending) and delete (expired) are the same soft-cancel; only the
  // wording differs, since an expired link is already dead.
  const cancelInvite = async (invite: OrgInvite, successMsg: string, failureMsg: string) => {
    try {
      await cancelMutation.mutateAsync({ inviteId: invite.id });
      toast.success(successMsg);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : failureMsg);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!inviteToDelete) return;
    const invite = inviteToDelete;
    setInviteToDelete(null);
    await cancelInvite(
      invite,
      t.settings.invitations.deleteInviteSuccess,
      t.settings.invitations.deleteInviteFailed
    );
  };

  const handleRevokeConfirm = async () => {
    if (!inviteToRevoke) return;
    const invite = inviteToRevoke;
    setInviteToRevoke(null);
    await cancelInvite(
      invite,
      t.settings.invitations.revokeInviteSuccess,
      t.settings.invitations.revokeInviteFailed
    );
  };

  const handleClearExpired = async () => {
    const inviteIds = expired.map((invite) => invite.id);
    setConfirmClearExpired(false);
    try {
      const cancelled = await clearExpiredMutation.mutateAsync({ inviteIds });
      if (cancelled.length < inviteIds.length) {
        toast.warning(t.settings.invitations.clearExpiredPartial);
      } else {
        toast.success(t.settings.invitations.clearExpiredSuccess);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t.settings.invitations.clearExpiredFailed);
    }
  };

  const handlers: InviteActionHandlers = {
    onCopyLink: handleCopyLink,
    onResend: (invite) => resendMutation.mutate({ inviteId: invite.id }),
    onRevoke: setInviteToRevoke,
    onDelete: setInviteToDelete,
    onClearExpired: () => setConfirmClearExpired(true),
    isResending: (invite) =>
      resendMutation.isPending && resendMutation.variables?.inviteId === invite.id,
    copiedId,
    clearingExpired: clearExpiredMutation.isPending,
  };

  return (
    <section className="space-y-6 pb-8">
      <div className="flex items-center justify-between gap-4">
        <SettingsSectionLabel>{t.settings.invitations.title}</SettingsSectionLabel>
        {canManageMembers && (
          <Button onClick={() => setShowInviteDialog(true)} {...pageInfoTarget("org.invite")}>
            <UserPlus className="mr-2 h-4 w-4" />
            {t.settings.invitations.inviteUser}
          </Button>
        )}
      </div>

      <InvitationsContent
        isLoading={isLoading}
        error={error}
        pending={pending}
        expired={expired}
        handlers={handlers}
      />

      <InviteUserDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        onInviteSent={refetchInvites}
      />

      <ConfirmDialog
        open={inviteToRevoke !== null}
        onOpenChange={(open) => !open && setInviteToRevoke(null)}
        onConfirm={handleRevokeConfirm}
        title={t.settings.invitations.revokeInviteTitle}
        description={t.settings.invitations.revokeInviteDescription.replace(
          "{email}",
          inviteToRevoke?.invitee_email ?? ""
        )}
        confirmText={t.settings.invitations.revoke}
        cancelText={t.common.cancel}
      />

      <ConfirmDialog
        open={inviteToDelete !== null}
        onOpenChange={(open) => !open && setInviteToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={t.settings.invitations.deleteInviteTitle}
        description={t.settings.invitations.deleteInviteDescription.replace(
          "{email}",
          inviteToDelete?.invitee_email ?? ""
        )}
        confirmText={t.common.delete}
        cancelText={t.common.cancel}
      />

      <ConfirmDialog
        open={confirmClearExpired}
        onOpenChange={(open) => !open && setConfirmClearExpired(false)}
        onConfirm={handleClearExpired}
        title={t.settings.invitations.clearExpiredTitle}
        description={t.settings.invitations.clearExpiredDescription.replace(
          "{count}",
          String(expired.length)
        )}
        confirmText={t.settings.invitations.clearAllExpired}
        cancelText={t.common.cancel}
      />
    </section>
  );
}

function InvitationsContent({
  isLoading,
  error,
  pending,
  expired,
  handlers,
}: {
  isLoading: boolean;
  error: unknown;
  pending: OrgInvite[];
  expired: OrgInvite[];
  handlers: InviteActionHandlers;
}): ReactNode {
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t.common.error}</AlertTitle>
        <AlertDescription>
          {error instanceof Error ? error.message : t.settings.invitations.failedLoadInvites}
        </AlertDescription>
      </Alert>
    );
  }

  if (pending.length === 0 && expired.length === 0) {
    return <InvitationsEmptyState />;
  }

  return (
    <div className="space-y-8">
      {pending.length > 0 && <PendingInviteGroup invites={pending} handlers={handlers} />}
      {expired.length > 0 && <ExpiredInviteGroup invites={expired} handlers={handlers} />}
    </div>
  );
}
